import { v4 as uuidv4 } from 'uuid';

/**
 * @typedef {Object} BorderRadius
 * @property {number} topLeft - Top left corner radius in pixels
 * @property {number} topRight - Top right corner radius in pixels
 * @property {number} bottomLeft - Bottom left corner radius in pixels
 * @property {number} bottomRight - Bottom right corner radius in pixels
 */

/**
 * @typedef {Object} Component
 * @property {string} id - Unique identifier for the component
 * @property {string} type - Component type (logo, text, image, video, bullets)
 * @property {string} [content] - Content for text components or media ID for image/video
 * @property {number} x - X position in pixels from the left of the canvas
 * @property {number} y - Y position in pixels from the top of the canvas
 * @property {number|string} width - Width in pixels or 'auto'
 * @property {number|string} height - Height in pixels or 'auto'
 * @property {boolean} [preserveAspectRatio] - Whether to preserve aspect ratio during resizing
 * @property {BorderRadius} [borderRadius] - Border radius settings for the component
 * @property {string} [backgroundColor] - Background color in hex or CSS color name
 * @property {number} [opacity] - Opacity value between 0 and 1
 * @property {number} [fontSize] - Font size in pixels for text components
 * @property {boolean} [bold] - Whether text is bold
 * @property {boolean} [italic] - Whether text is italic
 * @property {boolean} [underline] - Whether text is underlined
 * @property {string} [color] - Text color in hex or CSS color name
 * @property {string} [direction] - Text direction ('ltr' or 'rtl')
 */

/**
 * @typedef {Object} BackgroundImage
 * @property {number} opacity - Opacity value between 0 and 1
 * @property {string} backgroundColor - Background color behind the image
 */

/**
 * @typedef {Object} Template
 * @property {string} id - Unique identifier for the template
 * @property {string} nameKey - Translation key for the template name
 * @property {string} descriptionKey - Translation key for the template description
 * @property {string} thumbnail - CSS class name for the thumbnail display
 * @property {Component[]} components - Array of components in the template
 * @property {BackgroundImage} [backgroundImage] - Background image settings
 */

/**
 * Creates standard slide template components with translated content
 * 
 * @param {Object} i18n - The i18n object from I18nReactContext
 * @param {string} direction - The text direction ('ltr' or 'rtl')
 * @returns {Component[]} Array of components with translated content
 */
const createStandardSlideComponents = (i18n, direction = 'ltr') => {
  // Calculate positions based on direction
  const isRTL = direction === 'rtl';
  const canvasWidth = 800; // Assuming canvas width is 800px

  // For RTL, we need to mirror the x positions
  const logoX = isRTL ? canvasWidth - 20 - 150 : 20; // 20 from left or right, width is 150
  const titleX = isRTL ? canvasWidth - 200 - 550 : 200; // 200 from left or right, width is 550
  const imageX = isRTL ? canvasWidth - 50 - 300 : 50; // 50 from left or right, width is 300
  const textX = isRTL ? canvasWidth - 400 - 350 : 400; // 400 from left or right, width is 350

  return [
    // Company logo component positioned in the top-left corner (or top-right for RTL)
    {
      id: uuidv4(),
      type: 'logo', // Uses the company logo from CompanyContext
      x: logoX, // Position from left edge (or right edge for RTL)
      y: 20, // Position from top edge
      width: 150, // Width in pixels
      height: 60, // Height in pixels
      preserveAspectRatio: true, // Maintains logo proportions when resized
      borderRadius: {
        topLeft: 0,
        topRight: 0,
        bottomLeft: 0,
        bottomRight: 0
      },
      backgroundColor: 'transparent', // No background
      opacity: 1, // Fully opaque
      direction: direction // Add direction property
    },

    // Slide title component positioned at the top
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.slideTitle'), // Translated title text
      x: titleX, // Positioned to the right of the logo (or left for RTL)
      y: 30, // Aligned with the logo vertically
      width: 550, // More space for title
      height: 60, // Fixed height considering space constraints
      fontSize: 28, // Large font for title
      bold: true, // Bold for emphasis
      italic: false,
      underline: false,
      color: '#333333', // Dark gray text
      backgroundColor: 'transparent', // No background
      opacity: 1, // Fully opaque
      direction: direction // Add direction property
    },

    // Image component positioned on the left side of the content area (or right for RTL)
    {
      id: uuidv4(),
      type: 'image',
      content: '', // Empty initially, user will select an image
      x: imageX, // Left side of content area (or right for RTL)
      y: 120, // Below the title and logo
      width: 300, // Medium width
      height: 250, // Proportional height
      preserveAspectRatio: true, // Maintains image proportions when resized
      borderRadius: {
        topLeft: 8, // Slightly rounded corners
        topRight: 8,
        bottomLeft: 8,
        bottomRight: 8
      },
      backgroundColor: '#f5f5f5', // Light gray background before image is added
      opacity: 1, // Fully opaque
      direction: direction // Add direction property
    },

    // Text content component positioned on the right side of the content area (or left for RTL)
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.loremIpsum'), // Translated placeholder text
      x: textX, // Right side of content area, next to the image (or left for RTL)
      y: 120, // Aligned with the image vertically
      width: 350, // Width allows for a few paragraphs
      height: 250, // Fixed height considering available space to bottom (450 - 120 - 80 margin)
      fontSize: 16, // Standard reading size
      bold: false,
      italic: false,
      underline: false,
      color: '#333333', // Dark gray text
      backgroundColor: 'transparent', // No background
      opacity: 1, // Fully opaque
      direction: direction // Add direction property
    }
  ];
};

/**
 * Creates video-focused template components with translated content
 * 
 * @param {Object} i18n - The i18n object from I18nReactContext
 * @param {string} direction - The text direction ('ltr' or 'rtl')
 * @returns {Component[]} Array of components with translated content
 */
const createVideoFocusedComponents = (i18n, direction = 'ltr') => {
  // Calculate positions based on direction
  const isRTL = direction === 'rtl';
  const canvasWidth = 800; // Assuming canvas width is 800px

  // For RTL, we need to mirror the x positions
  const logoX = isRTL ? canvasWidth - 20 - 150 : 20; // 20 from left or right, width is 150
  const titleX = isRTL ? canvasWidth - 200 - 550 : 200; // 200 from left or right, width is 550
  const videoX = isRTL ? canvasWidth - 150 - 500 : 150; // 150 from left or right, width is 500
  const textX = isRTL ? canvasWidth - 150 - 500 : 150; // 150 from left or right, width is 500

  return [
    // Company logo component positioned in the top-left corner (or top-right for RTL)
    {
      id: uuidv4(),
      type: 'logo',
      x: logoX,
      y: 20,
      width: 150,
      height: 60,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 0,
        topRight: 0,
        bottomLeft: 0,
        bottomRight: 0
      },
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction // Add direction property
    },

    // Slide title component positioned at the top
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.videoTitle'),
      x: titleX,
      y: 30,
      width: 550,
      height: 60,
      fontSize: 28,
      bold: true,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction // Add direction property
    },

    // Video component positioned in the center
    {
      id: uuidv4(),
      type: 'video',
      content: '',
      x: videoX,
      y: 100,
      width: 500,
      height: 280,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 8,
        topRight: 8,
        bottomLeft: 8,
        bottomRight: 8
      },
      backgroundColor: '#f5f5f5',
      opacity: 1,
      direction: direction // Add direction property
    },

    // Text content component positioned below the video
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.videoDescription'),
      x: textX,
      y: 400,
      width: 500,
      height: 40, // Fixed height considering available space to bottom (450 - 400 - 10 margin)
      fontSize: 16,
      bold: false,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction // Add direction property
    }
  ];
};

/**
 * Creates bullet points template components with translated content
 * 
 * @param {Object} i18n - The i18n object from I18nReactContext
 * @param {string} direction - The text direction ('ltr' or 'rtl')
 * @returns {Component[]} Array of components with translated content
 */
const createBulletPointsComponents = (i18n, direction = 'ltr') => {
  // Calculate positions based on direction
  const isRTL = direction === 'rtl';
  const canvasWidth = 800; // Assuming canvas width is 800px

  // For RTL, we need to mirror the x positions
  const logoX = isRTL ? canvasWidth - 20 - 150 : 20; // 20 from left or right, width is 150
  const titleX = isRTL ? canvasWidth - 200 - 550 : 200; // 200 from left or right, width is 550
  const bulletsX = isRTL ? canvasWidth - 150 - 500 : 150; // 150 from left or right, width is 500

  return [
    // Company logo component positioned in the top-left corner (or top-right for RTL)
    {
      id: uuidv4(),
      type: 'logo',
      x: logoX,
      y: 20,
      width: 150,
      height: 60,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 0,
        topRight: 0,
        bottomLeft: 0,
        bottomRight: 0
      },
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction // Add direction property
    },

    // Slide title component positioned at the top
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.bulletTitle'),
      x: titleX,
      y: 30,
      width: 550,
      height: 60,
      fontSize: 28,
      bold: true,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction // Add direction property
    },

    // Bullet list component positioned in the center
    {
      id: uuidv4(),
      type: 'bullets',
      content: [
        i18n.translate('weezbeeCourse.templateBank.content.bullet1'),
        i18n.translate('weezbeeCourse.templateBank.content.bullet2'),
        i18n.translate('weezbeeCourse.templateBank.content.bullet3')
      ],
      x: bulletsX,
      y: 120,
      width: 500,
      height: 300, // Fixed height considering available space to bottom (450 - 120 - 30 margin)
      fontSize: 18,
      bold: false,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction // Add direction property
    }
  ];
};

/**
 * Creates centered image template components with translated content
 * 
 * @param {Object} i18n - The i18n object from I18nReactContext
 * @param {string} direction - The text direction ('ltr' or 'rtl')
 * @returns {Component[]} Array of components with translated content
 */
const createCenteredImageComponents = (i18n, direction = 'ltr') => {
  // Calculate positions based on direction
  const isRTL = direction === 'rtl';
  const canvasWidth = 800; // Assuming canvas width is 800px

  // For RTL, we need to mirror the x positions
  const logoX = isRTL ? canvasWidth - 20 - 150 : 20; // 20 from left or right, width is 150
  const titleX = isRTL ? canvasWidth - 200 - 550 : 200; // 200 from left or right, width is 550
  const imageX = isRTL ? canvasWidth - 200 - 550 : 200; // 200 from left or right, width is 550
  const captionX = isRTL ? canvasWidth - 200 - 550 : 200; // 200 from left or right, width is 550

  return [
    // Company logo component positioned in the top-left corner (or top-right for RTL)
    {
      id: uuidv4(),
      type: 'logo',
      x: logoX,
      y: 20,
      width: 150,
      height: 60,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 0,
        topRight: 0,
        bottomLeft: 0,
        bottomRight: 0
      },
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction // Add direction property
    },

    // Slide title component positioned at the top
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.imageTitle'),
      x: titleX,
      y: 30,
      width: 550,
      height: 60,
      fontSize: 28,
      bold: true,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction // Add direction property
    },

    // Image component positioned in the center
    {
      id: uuidv4(),
      type: 'image',
      content: '',
      x: imageX,
      y: 100,
      width: 550,
      height: 250,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 8,
        topRight: 8,
        bottomLeft: 8,
        bottomRight: 8
      },
      backgroundColor: '#f5f5f5',
      opacity: 1,
      direction: direction // Add direction property
    },

    // Text content component positioned below the image
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.imageCaption'),
      x: captionX,
      y: 370,
      width: 550,
      height: 70, // Fixed height considering available space to bottom (450 - 370 - 10 margin)
      fontSize: 16,
      bold: false,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction // Add direction property
    }
  ];
};

/**
 * Creates text and bullets template components with translated content
 * 
 * @param {Object} i18n - The i18n object from I18nReactContext
 * @param {string} direction - The text direction ('ltr' or 'rtl')
 * @returns {Component[]} Array of components with translated content
 */
const createTextAndBulletsComponents = (i18n, direction = 'ltr') => {
  // Calculate positions based on direction
  const isRTL = direction === 'rtl';
  const canvasWidth = 800; // Assuming canvas width is 800px

  // For RTL, we need to mirror the x positions
  const logoX = isRTL ? canvasWidth - 20 - 150 : 20; // 20 from left or right, width is 150
  const titleX = isRTL ? canvasWidth - 200 - 550 : 200; // 200 from left or right, width is 550

  // For RTL, we swap the positions of text and bullets
  const textX = isRTL ? canvasWidth - 50 - 350 : 50; // 50 from left or right, width is 350
  const bulletsX = isRTL ? canvasWidth - 420 - 350 : 420; // 420 from left or right, width is 350

  return [
    // Company logo component positioned in the top-left corner (or top-right for RTL)
    {
      id: uuidv4(),
      type: 'logo',
      x: logoX,
      y: 20,
      width: 150,
      height: 60,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 0,
        topRight: 0,
        bottomLeft: 0,
        bottomRight: 0
      },
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction // Add direction property
    },

    // Slide title component positioned at the top
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.textBulletTitle'),
      x: titleX,
      y: 30,
      width: 550,
      height: 60,
      fontSize: 28,
      bold: true,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction // Add direction property
    },

    // Text content component positioned on the left (or right for RTL)
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.textContent'),
      x: textX,
      y: 120,
      width: 350,
      height: 300, // Fixed height considering available space to bottom (450 - 120 - 30 margin)
      fontSize: 16,
      bold: false,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction // Add direction property
    },

    // Bullet list component positioned on the right (or left for RTL)
    {
      id: uuidv4(),
      type: 'bullets',
      content: [
        i18n.translate('weezbeeCourse.templateBank.content.textBullet1'),
        i18n.translate('weezbeeCourse.templateBank.content.textBullet2'),
        i18n.translate('weezbeeCourse.templateBank.content.textBullet3')
      ],
      x: bulletsX,
      y: 120,
      width: 350,
      height: 300, // Fixed height considering available space to bottom (450 - 120 - 30 margin)
      fontSize: 16,
      bold: false,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction // Add direction property
    }
  ];
};

/**
 * Creates video and bullets template components with translated content
 * 
 * @param {Object} i18n - The i18n object from I18nReactContext
 * @param {string} direction - The text direction ('ltr' or 'rtl')
 * @returns {Component[]} Array of components with translated content
 */
const createVideoAndBulletsComponents = (i18n, direction = 'ltr') => {
  // Calculate positions based on direction
  const isRTL = direction === 'rtl';
  const canvasWidth = 800; // Assuming canvas width is 800px

  // For RTL, we need to mirror the x positions
  const logoX = isRTL ? canvasWidth - 20 - 150 : 20; // 20 from left or right, width is 150
  const titleX = isRTL ? canvasWidth - 200 - 550 : 200; // 200 from left or right, width is 550

  // For RTL, we swap the positions of video and bullets
  const videoX = isRTL ? canvasWidth - 50 - 350 : 50; // 50 from left or right, width is 350
  const bulletsX = isRTL ? canvasWidth - 420 - 350 : 420; // 420 from left or right, width is 350

  return [
    // Company logo component positioned in the top-left corner (or top-right for RTL)
    {
      id: uuidv4(),
      type: 'logo',
      x: logoX,
      y: 20,
      width: 150,
      height: 60,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 0,
        topRight: 0,
        bottomLeft: 0,
        bottomRight: 0
      },
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction // Add direction property
    },

    // Slide title component positioned at the top
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.videoBulletTitle'),
      x: titleX,
      y: 30,
      width: 550,
      height: 60,
      fontSize: 28,
      bold: true,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction // Add direction property
    },

    // Video component positioned on the left (or right for RTL)
    {
      id: uuidv4(),
      type: 'video',
      content: '',
      x: videoX,
      y: 120,
      width: 350,
      height: 200,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 8,
        topRight: 8,
        bottomLeft: 8,
        bottomRight: 8
      },
      backgroundColor: '#f5f5f5',
      opacity: 1,
      direction: direction // Add direction property
    },

    // Bullet list component positioned on the right (or left for RTL)
    {
      id: uuidv4(),
      type: 'bullets',
      content: [
        i18n.translate('weezbeeCourse.templateBank.content.videoBullet1'),
        i18n.translate('weezbeeCourse.templateBank.content.videoBullet2'),
        i18n.translate('weezbeeCourse.templateBank.content.videoBullet3')
      ],
      x: bulletsX,
      y: 120,
      width: 350,
      height: 300, // Fixed height considering available space to bottom (450 - 120 - 30 margin)
      fontSize: 16,
      bold: false,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction // Add direction property
    }
  ];
};

/**
 * Creates two-column image template components with translated content
 * 
 * @param {Object} i18n - The i18n object from I18nReactContext
 * @param {string} direction - The text direction ('ltr' or 'rtl')
 * @returns {Component[]} Array of components with translated content
 */
const createTwoColumnImageComponents = (i18n, direction = 'ltr') => {
  // Calculate positions based on direction
  const isRTL = direction === 'rtl';
  const canvasWidth = 800; // Assuming canvas width is 800px

  // For RTL, we need to mirror the x positions
  const logoX = isRTL ? canvasWidth - 20 - 150 : 20; // 20 from left or right, width is 150
  const titleX = isRTL ? canvasWidth - 200 - 550 : 200; // 200 from left or right, width is 400
  const image1X = isRTL ? canvasWidth - 50 - 350 : 50; // 50 from left or right, width is 350
  const image2X = isRTL ? canvasWidth - 420 - 350 : 420; // 420 from left or right, width is 350
  const textX = isRTL ? canvasWidth - 200 - 400 : 200; // 200 from left or right, width is 400

  return [
    // Company logo component positioned in the top-left corner (or top-right for RTL)
    {
      id: uuidv4(),
      type: 'logo',
      x: logoX,
      y: 20,
      width: 150,
      height: 60,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 0,
        topRight: 0,
        bottomLeft: 0,
        bottomRight: 0
      },
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction
    },

    // Slide title component positioned at the top
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.twoColumnImageTitle'),
      x: titleX,
      y: 30,
      width: 550,
      height: 60,
      fontSize: 28,
      bold: true,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction
    },

    // First image component positioned on the left (or right for RTL)
    {
      id: uuidv4(),
      type: 'image',
      content: '',
      x: image1X,
      y: 120,
      width: 350,
      height: 200,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 8,
        topRight: 8,
        bottomLeft: 8,
        bottomRight: 8
      },
      backgroundColor: '#f5f5f5',
      opacity: 1,
      direction: direction
    },

    // Second image component positioned on the right (or left for RTL)
    {
      id: uuidv4(),
      type: 'image',
      content: '',
      x: image2X,
      y: 120,
      width: 350,
      height: 200,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 8,
        topRight: 8,
        bottomLeft: 8,
        bottomRight: 8
      },
      backgroundColor: '#f5f5f5',
      opacity: 1,
      direction: direction
    },

    // Text content component positioned below the images
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.twoColumnImageCaption'),
      x: textX,
      y: 340,
      width: 550,
      height: 100, // Fixed height considering available space to bottom (450 - 340 - 10 margin)
      fontSize: 16,
      bold: false,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction
    }
  ];
};

/**
 * Creates three-column text template components with translated content
 * 
 * @param {Object} i18n - The i18n object from I18nReactContext
 * @param {string} direction - The text direction ('ltr' or 'rtl')
 * @returns {Component[]} Array of components with translated content
 */
const createThreeColumnTextComponents = (i18n, direction = 'ltr') => {
  // Calculate positions based on direction
  const isRTL = direction === 'rtl';
  const canvasWidth = 800; // Assuming canvas width is 800px

  // For RTL, we need to mirror the x positions
  const logoX = isRTL ? canvasWidth - 20 - 150 : 20; // 20 from left or right, width is 150
  const titleX = isRTL ? canvasWidth - 200 - 550 : 200; // 200 from left or right, width is 550

  // Column positions
  const column1X = isRTL ? canvasWidth - 50 - 230 : 50; // 50 from left or right, width is 230
  const column2X = isRTL ? canvasWidth - 290 - 230 : 290; // 290 from left or right, width is 230
  const column3X = isRTL ? canvasWidth - 530 - 230 : 530; // 530 from left or right, width is 230

  return [
    // Company logo component positioned in the top-left corner (or top-right for RTL)
    {
      id: uuidv4(),
      type: 'logo',
      x: logoX,
      y: 20,
      width: 150,
      height: 60,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 0,
        topRight: 0,
        bottomLeft: 0,
        bottomRight: 0
      },
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction
    },

    // Slide title component positioned at the top
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.threeColumnTitle'),
      x: titleX,
      y: 30,
      width: 550,
      height: 60,
      fontSize: 28,
      bold: true,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction
    },

    // First column text
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.column1Text'),
      x: column1X,
      y: 120,
      width: 230,
      height: 300, // Fixed height considering available space to bottom (450 - 120 - 30 margin)
      fontSize: 16,
      bold: true,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction
    },

    // Second column text
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.column2Text'),
      x: column2X,
      y: 120,
      width: 230,
      height: 300, // Fixed height considering available space to bottom (450 - 120 - 30 margin)
      fontSize: 16,
      bold: true,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction
    },

    // Third column text
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.column3Text'),
      x: column3X,
      y: 120,
      width: 230,
      height: 300, // Fixed height considering available space to bottom (450 - 120 - 30 margin)
      fontSize: 16,
      bold: true,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction
    }
  ];
};

/**
 * Creates image gallery template components with translated content
 * 
 * @param {Object} i18n - The i18n object from I18nReactContext
 * @param {string} direction - The text direction ('ltr' or 'rtl')
 * @returns {Component[]} Array of components with translated content
 */
const createImageGalleryComponents = (i18n, direction = 'ltr') => {
  // Calculate positions based on direction
  const isRTL = direction === 'rtl';
  const canvasWidth = 800; // Assuming canvas width is 800px

  // For RTL, we need to mirror the x positions
  const logoX = isRTL ? canvasWidth - 20 - 150 : 20; // 20 from left or right, width is 150
  const titleX = isRTL ? canvasWidth - 200 - 550 : 200; // 200 from left or right, width is 550

  // Image positions in a 2x2 grid
  const image1X = isRTL ? canvasWidth - 150 - 230 : 150; // Top-left image
  const image2X = isRTL ? canvasWidth - 420 - 230 : 420; // Top-right image
  const image3X = isRTL ? canvasWidth - 150 - 230 : 150; // Bottom-left image
  const image4X = isRTL ? canvasWidth - 420 - 230 : 420; // Bottom-right image

  // Caption position
  const captionX = isRTL ? canvasWidth - 200 - 550 : 200;

  return [
    // Company logo component positioned in the top-left corner (or top-right for RTL)
    {
      id: uuidv4(),
      type: 'logo',
      x: logoX,
      y: 20,
      width: 150,
      height: 60,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 0,
        topRight: 0,
        bottomLeft: 0,
        bottomRight: 0
      },
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction
    },

    // Slide title component positioned at the top
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.imageGalleryTitle'),
      x: titleX,
      y: 30,
      width: 550,
      height: 60,
      fontSize: 28,
      bold: true,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction
    },

    // Image 1 (top-left)
    {
      id: uuidv4(),
      type: 'image',
      content: '',
      x: image1X,
      y: 120,
      width: 230,
      height: 150,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 8,
        topRight: 8,
        bottomLeft: 8,
        bottomRight: 8
      },
      backgroundColor: '#f5f5f5',
      opacity: 1,
      direction: direction
    },

    // Image 2 (top-right)
    {
      id: uuidv4(),
      type: 'image',
      content: '',
      x: image2X,
      y: 120,
      width: 230,
      height: 150,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 8,
        topRight: 8,
        bottomLeft: 8,
        bottomRight: 8
      },
      backgroundColor: '#f5f5f5',
      opacity: 1,
      direction: direction
    },

    // Image 3 (bottom-left)
    {
      id: uuidv4(),
      type: 'image',
      content: '',
      x: image3X,
      y: 280,
      width: 230,
      height: 150,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 8,
        topRight: 8,
        bottomLeft: 8,
        bottomRight: 8
      },
      backgroundColor: '#f5f5f5',
      opacity: 1,
      direction: direction
    },

    // Image 4 (bottom-right)
    {
      id: uuidv4(),
      type: 'image',
      content: '',
      x: image4X,
      y: 280,
      width: 230,
      height: 150,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 8,
        topRight: 8,
        bottomLeft: 8,
        bottomRight: 8
      },
      backgroundColor: '#f5f5f5',
      opacity: 1,
      direction: direction
    },

    // Caption text
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.imageGalleryCaption'),
      x: captionX,
      y: 440,
      width: 550,
      height: 10, // Fixed height considering available space to bottom (450 - 440)
      fontSize: 16,
      bold: false,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction
    }
  ];
};

/**
 * Creates video with text overlay template components with translated content
 * 
 * @param {Object} i18n - The i18n object from I18nReactContext
 * @param {string} direction - The text direction ('ltr' or 'rtl')
 * @returns {Component[]} Array of components with translated content
 */
const createVideoWithOverlayComponents = (i18n, direction = 'ltr') => {
  // Calculate positions based on direction
  const isRTL = direction === 'rtl';
  const canvasWidth = 800; // Assuming canvas width is 800px

  // For RTL, we need to mirror the x positions
  const logoX = isRTL ? canvasWidth - 20 - 150 : 20; // 20 from left or right, width is 150
  const titleX = isRTL ? canvasWidth - 200 - 550 : 200; // 200 from left or right, width is 550
  const videoX = isRTL ? canvasWidth - 150 - 500 : 150; // 150 from left or right, width is 500
  const overlayX = isRTL ? canvasWidth - 200 - 550 : 200; // 200 from left or right, width is 550

  return [
    // Company logo component positioned in the top-left corner (or top-right for RTL)
    {
      id: uuidv4(),
      type: 'logo',
      x: logoX,
      y: 20,
      width: 150,
      height: 60,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 0,
        topRight: 0,
        bottomLeft: 0,
        bottomRight: 0
      },
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction
    },

    // Slide title component positioned at the top
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.videoOverlayTitle'),
      x: titleX,
      y: 30,
      width: 550,
      height: 60,
      fontSize: 28,
      bold: true,
      italic: false,
      underline: false,
      color: '#333333',
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction
    },

    // Video component positioned in the center
    {
      id: uuidv4(),
      type: 'video',
      content: '',
      x: videoX,
      y: 100,
      width: 500,
      height: 280,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 8,
        topRight: 8,
        bottomLeft: 8,
        bottomRight: 8
      },
      backgroundColor: '#f5f5f5',
      opacity: 1,
      direction: direction
    },

    // Text overlay component positioned over the video
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.videoOverlayText'),
      x: overlayX,
      y: 320,
      width: 550,
      height: 130, // Fixed height considering available space to bottom (450 - 320)
      fontSize: 18,
      bold: true,
      italic: false,
      underline: false,
      color: '#ffffff',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      opacity: 0.9,
      direction: direction
    }
  ];
};

/**
 * Creates title with image background template components with translated content
 * 
 * @param {Object} i18n - The i18n object from I18nReactContext
 * @param {string} direction - The text direction ('ltr' or 'rtl')
 * @returns {Component[]} Array of components with translated content
 */
const createTitleWithBackgroundComponents = (i18n, direction = 'ltr') => {
  // Calculate positions based on direction
  const isRTL = direction === 'rtl';
  const canvasWidth = 800; // Assuming canvas width is 800px

  // For RTL, we need to mirror the x positions
  const logoX = isRTL ? canvasWidth - 20 - 150 : 20; // 20 from left or right, width is 150
  const titleX = isRTL ? canvasWidth - 200 - 550 : 200; // 200 from left or right, width is 550
  const subtitleX = isRTL ? canvasWidth - 200 - 550 : 200; // 200 from left or right, width is 550
  const textX = isRTL ? canvasWidth - 150 - 500 : 150; // 150 from left or right, width is 500

  return [
    // Company logo component positioned in the top-left corner (or top-right for RTL)
    {
      id: uuidv4(),
      type: 'logo',
      x: logoX,
      y: 20,
      width: 150,
      height: 60,
      preserveAspectRatio: true,
      borderRadius: {
        topLeft: 0,
        topRight: 0,
        bottomLeft: 0,
        bottomRight: 0
      },
      backgroundColor: 'transparent',
      opacity: 1,
      direction: direction
    },

    // Large title component positioned at the top center
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.backgroundTitle'),
      x: titleX,
      y: 150,
      width: 550,
      height: 60, // Fixed height considering space constraints
      fontSize: 36,
      bold: true,
      italic: false,
      underline: false,
      color: '#ffffff',
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      opacity: 1,
      direction: direction
    },

    // Subtitle component positioned below the title
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.backgroundSubtitle'),
      x: subtitleX,
      y: 220,
      width: 550,
      height: 50, // Fixed height considering space constraints
      fontSize: 24,
      bold: false,
      italic: true,
      underline: false,
      color: '#ffffff',
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      opacity: 1,
      direction: direction
    },

    // Text content component positioned at the bottom
    {
      id: uuidv4(),
      type: 'text',
      content: i18n.translate('weezbeeCourse.templateBank.content.backgroundText'),
      x: textX,
      y: 350,
      width: 500,
      height: 100, // Fixed height considering available space to bottom (450 - 350)
      fontSize: 18,
      bold: false,
      italic: false,
      underline: false,
      color: '#ffffff',
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      opacity: 1,
      direction: direction
    }
  ];
};

/**
 * Array of predefined templates for creating new chapters
 * @type {Template[]}
 */
export const templates = [
  /**
   * Standard Slide Template
   * 
   * This template creates a professional slide layout with:
   * - Company logo in the top-left corner
   * - Large title at the top
   * - Image on the left side of the content area
   * - Text content on the right side
   * - Optional background image with low opacity
   * 
   * This layout follows common presentation design principles with a clear
   * visual hierarchy and balanced content placement.
   */
  {
    id: 'standard-slide',
    nameKey: 'weezbeeCourse.templateBank.templates.standardSlide.name',
    descriptionKey: 'weezbeeCourse.templateBank.templates.standardSlide.description',
    thumbnail: 'standard-slide-thumbnail', // CSS class for the thumbnail preview
    getComponents: createStandardSlideComponents,
    // Background image settings - when a user adds a background image, these settings will be applied
    backgroundImage: {
      opacity: 0.1, // Very light opacity to not distract from content
      backgroundColor: '#ffffff' // White background behind the image
    }
  },

  /**
   * Video Focused Template
   * 
   * This template creates a layout focused on video content:
   * - Company logo in the top-left corner
   * - Large title at the top
   * - Large video in the center
   * - Supporting text below the video
   */
  {
    id: 'video-focused',
    nameKey: 'weezbeeCourse.templateBank.templates.videoFocused.name',
    descriptionKey: 'weezbeeCourse.templateBank.templates.videoFocused.description',
    thumbnail: 'video-focused-thumbnail',
    getComponents: createVideoFocusedComponents,
    backgroundImage: {
      opacity: 0.1,
      backgroundColor: '#ffffff'
    }
  },

  /**
   * Bullet Points Template
   * 
   * This template creates a layout focused on bullet points:
   * - Company logo in the top-left corner
   * - Large title at the top
   * - Bullet list in the center
   */
  {
    id: 'bullet-points',
    nameKey: 'weezbeeCourse.templateBank.templates.bulletPoints.name',
    descriptionKey: 'weezbeeCourse.templateBank.templates.bulletPoints.description',
    thumbnail: 'bullet-points-thumbnail',
    getComponents: createBulletPointsComponents,
    backgroundImage: {
      opacity: 0.1,
      backgroundColor: '#ffffff'
    }
  },

  /**
   * Centered Image Template
   * 
   * This template creates a layout with a centered image:
   * - Company logo in the top-left corner
   * - Large title at the top
   * - Large image in the center
   * - Caption text below the image
   */
  {
    id: 'centered-image',
    nameKey: 'weezbeeCourse.templateBank.templates.centeredImage.name',
    descriptionKey: 'weezbeeCourse.templateBank.templates.centeredImage.description',
    thumbnail: 'centered-image-thumbnail',
    getComponents: createCenteredImageComponents,
    backgroundImage: {
      opacity: 0.1,
      backgroundColor: '#ffffff'
    }
  },

  /**
   * Text and Bullets Template
   * 
   * This template creates a layout with text and bullets side by side:
   * - Company logo in the top-left corner
   * - Large title at the top
   * - Text content on the left
   * - Bullet list on the right
   */
  {
    id: 'text-and-bullets',
    nameKey: 'weezbeeCourse.templateBank.templates.textAndBullets.name',
    descriptionKey: 'weezbeeCourse.templateBank.templates.textAndBullets.description',
    thumbnail: 'text-and-bullets-thumbnail',
    getComponents: createTextAndBulletsComponents,
    backgroundImage: {
      opacity: 0.1,
      backgroundColor: '#ffffff'
    }
  },

  /**
   * Video and Bullets Template
   * 
   * This template creates a layout with video and bullets side by side:
   * - Company logo in the top-left corner
   * - Large title at the top
   * - Video on the left
   * - Bullet list on the right
   */
  {
    id: 'video-and-bullets',
    nameKey: 'weezbeeCourse.templateBank.templates.videoAndBullets.name',
    descriptionKey: 'weezbeeCourse.templateBank.templates.videoAndBullets.description',
    thumbnail: 'video-and-bullets-thumbnail',
    getComponents: createVideoAndBulletsComponents,
    backgroundImage: {
      opacity: 0.1,
      backgroundColor: '#ffffff'
    }
  },

  /**
   * Two-Column Image Template
   * 
   * This template creates a layout with two images side by side:
   * - Company logo in the top-left corner
   * - Large title at the top
   * - Two images side by side in the center
   * - Caption text below the images
   */
  {
    id: 'two-column-image',
    nameKey: 'weezbeeCourse.templateBank.templates.twoColumnImage.name',
    descriptionKey: 'weezbeeCourse.templateBank.templates.twoColumnImage.description',
    thumbnail: 'two-column-image-thumbnail',
    getComponents: createTwoColumnImageComponents,
    backgroundImage: {
      opacity: 0.1,
      backgroundColor: '#ffffff'
    }
  },

  /**
   * Three-Column Text Template
   * 
   * This template creates a layout with three text columns:
   * - Company logo in the top-left corner
   * - Large title at the top
   * - Three text columns below
   */
  {
    id: 'three-column-text',
    nameKey: 'weezbeeCourse.templateBank.templates.threeColumnText.name',
    descriptionKey: 'weezbeeCourse.templateBank.templates.threeColumnText.description',
    thumbnail: 'three-column-text-thumbnail',
    getComponents: createThreeColumnTextComponents,
    backgroundImage: {
      opacity: 0.1,
      backgroundColor: '#ffffff'
    }
  },

  /**
   * Image Gallery Template
   * 
   * This template creates a layout with a grid of images:
   * - Company logo in the top-left corner
   * - Large title at the top
   * - Four images in a 2x2 grid
   * - Caption text below the grid
   */
  {
    id: 'image-gallery',
    nameKey: 'weezbeeCourse.templateBank.templates.imageGallery.name',
    descriptionKey: 'weezbeeCourse.templateBank.templates.imageGallery.description',
    thumbnail: 'image-gallery-thumbnail',
    getComponents: createImageGalleryComponents,
    backgroundImage: {
      opacity: 0.1,
      backgroundColor: '#ffffff'
    }
  },

  /**
   * Video with Text Overlay Template
   * 
   * This template creates a layout with a video and text overlay:
   * - Company logo in the top-left corner
   * - Large title at the top
   * - Large video in the center
   * - Text overlay on the video
   */
  {
    id: 'video-with-overlay',
    nameKey: 'weezbeeCourse.templateBank.templates.videoWithOverlay.name',
    descriptionKey: 'weezbeeCourse.templateBank.templates.videoWithOverlay.description',
    thumbnail: 'video-with-overlay-thumbnail',
    getComponents: createVideoWithOverlayComponents,
    backgroundImage: {
      opacity: 0.1,
      backgroundColor: '#ffffff'
    }
  },

  /**
   * Title with Image Background Template
   * 
   * This template creates a layout with a title and text over a background image:
   * - Company logo in the top-left corner
   * - Large title in the center
   * - Subtitle below the title
   * - Text content at the bottom
   * - Background image covering the entire slide
   */
  {
    id: 'title-with-background',
    nameKey: 'weezbeeCourse.templateBank.templates.titleWithBackground.name',
    descriptionKey: 'weezbeeCourse.templateBank.templates.titleWithBackground.description',
    thumbnail: 'title-with-background-thumbnail',
    getComponents: createTitleWithBackgroundComponents,
    backgroundImage: {
      opacity: 0.5, // Higher opacity for this template
      backgroundColor: '#000000' // Dark background to make text more visible
    }
  }
];
